import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

export interface DropdownItem {
  label: string;
  value: string;
  icon?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  danger?: boolean;
}

export interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  onSelect: (value: string) => void;
  selectedValue?: string;
  align?: 'left' | 'right';
  width?: 'auto' | number;
  /** A small caption above the items, e.g. "Create". */
  label?: string;
}

/** Gap between trigger and menu, and the margin kept from the viewport edge. */
const GAP = 4;
const MARGIN = 8;

interface Placement {
  top: number;
  left: number;
  /** Which way it opened — picks the entrance animation. */
  above: boolean;
}

export function Dropdown({
  trigger,
  items,
  onSelect,
  selectedValue,
  align = 'left',
  width = 'auto',
  label,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Null until measured: the menu renders invisibly for one frame so it can be
  // measured, then appears already in the right place.
  const [placement, setPlacement] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  // Opened from the keyboard, focus lands on the first item, ready for Enter.
  // Opened with the pointer it lands on the menu itself, so no item is lit
  // before one has been pointed at — the arrow keys work either way.
  const openedByKeyboard = useRef(false);

  const close = useCallback((restoreFocus: boolean) => {
    setIsOpen(false);
    if (!restoreFocus) return;
    // Hand focus back to the control that opened the menu, so the keyboard
    // carries on from where it was.
    const opener = triggerRef.current?.querySelector<HTMLElement>(
      'button, [href], [tabindex]:not([tabindex="-1"])'
    );
    opener?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement(null);
      return;
    }
    const triggerEl = triggerRef.current;
    const menuEl = menuRef.current;
    if (!triggerEl || !menuEl) return;

    // The menu is position: fixed and getBoundingClientRect() is already
    // viewport-relative, so DO NOT add window.scroll offsets — doing so pushes
    // the menu off-screen once the page is scrolled (its own coordinate space
    // is the viewport, not the document).
    const t = triggerEl.getBoundingClientRect();
    const m = menuEl.getBoundingClientRect();

    // Open downward unless there is no room below and there is room above.
    const fitsBelow = t.bottom + GAP + m.height <= window.innerHeight - MARGIN;
    const fitsAbove = t.top - GAP - m.height >= MARGIN;
    const above = !fitsBelow && fitsAbove;
    const top = above ? t.top - GAP - m.height : t.bottom + GAP;

    const wanted = align === 'right' ? t.right - m.width : t.left;
    const left = Math.max(MARGIN, Math.min(wanted, window.innerWidth - m.width - MARGIN));

    setPlacement({ top, left, above });
  }, [isOpen, align, items.length]);

  // Move focus into the menu once it is placed.
  useEffect(() => {
    if (!isOpen || !placement) return;
    const menuEl = menuRef.current;
    if (!menuEl || menuEl.contains(document.activeElement)) return;
    if (!openedByKeyboard.current) {
      menuEl.focus({ preventScroll: true });
      return;
    }
    const options = Array.from(
      menuEl.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')
    );
    const selected = options.find((el) => el.dataset.selected === 'true');
    (selected ?? options[0] ?? menuEl).focus({ preventScroll: true });
  }, [isOpen, placement]);

  // A fixed menu doesn't follow the trigger on scroll — close it so it can't
  // hang detached in the viewport. Capture phase catches scrolls on any ancestor.
  useEffect(() => {
    if (!isOpen) return;
    const onScroll = (event: Event) => {
      // Scrolling the menu's own overflow is not the page moving.
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };
    const onResize = () => setIsOpen(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Capture phase + preventDefault: this Escape belongs to the menu. A
        // dialog the menu sits inside sees it was handled and stays open.
        event.preventDefault();
        event.stopPropagation();
        close(true);
        return;
      }
      if (event.key === 'Tab') {
        setIsOpen(false);
        return;
      }

      const menuEl = menuRef.current;
      if (!menuEl) return;
      const options = Array.from(
        menuEl.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')
      );
      if (options.length === 0) return;
      const current = options.indexOf(document.activeElement as HTMLButtonElement);

      let next: number;
      switch (event.key) {
        case 'ArrowDown':
          next = current < 0 ? 0 : (current + 1) % options.length;
          break;
        case 'ArrowUp':
          next = current < 0 ? options.length - 1 : (current - 1 + options.length) % options.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = options.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      options[next].focus();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, close]);

  const handleSelect = (value: string, disabled?: boolean) => {
    if (disabled) return;
    onSelect(value);
    close(true);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onClick={(event) => {
          // A click with no pointer behind it (detail 0) came from Enter or Space.
          openedByKeyboard.current = event.detail === 0;
          setIsOpen(!isOpen);
        }}
      >
        {/* The state belongs on the control itself, not on this wrapper — and
            only on a real button: on a bare <span> these attributes are
            invalid ARIA rather than merely unused. */}
        {React.isValidElement<React.HTMLAttributes<HTMLElement>>(trigger) && trigger.type === 'button'
          ? React.cloneElement(trigger, {
              'aria-haspopup': 'menu',
              'aria-expanded': isOpen,
              'aria-controls': isOpen ? menuId : undefined,
            })
          : trigger}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            tabIndex={-1}
            className={cn(
              'focus:outline-none',
              'fixed z-50 max-h-[min(24rem,calc(100vh-1rem))] overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-elevated',
              'dark:border-gray-700 dark:bg-gray-800',
              placement && (placement.above ? 'animate-fade-in-up' : 'animate-fade-in')
            )}
            style={{
              top: placement?.top ?? 0,
              left: placement?.left ?? 0,
              visibility: placement ? 'visible' : 'hidden',
              minWidth: typeof width === 'number' ? width : 180,
            }}
          >
            {label && (
              <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                {label}
              </div>
            )}
            {items.map((item) => {
              const isSelected = selectedValue === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  role="menuitem"
                  data-selected={isSelected || undefined}
                  className={cn(
                    'w-full rounded-lg px-2.5 py-2 text-left flex items-center gap-2.5 transition-colors',
                    'focus:outline-none focus-visible:outline-none',
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : item.danger
                      ? 'text-danger-600 dark:text-danger-400 hover:bg-danger-50 focus:bg-danger-50 dark:hover:bg-danger-900/30 dark:focus:bg-danger-900/30'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 focus:bg-gray-100 dark:hover:bg-gray-700 dark:focus:bg-gray-700',
                    isSelected && 'bg-primary-50 dark:bg-primary-900/30'
                  )}
                  onClick={() => handleSelect(item.value, item.disabled)}
                  disabled={item.disabled}
                >
                  {item.icon && (
                    <span
                      className={cn(
                        'flex-shrink-0 w-5 h-5 flex items-center justify-center',
                        !item.danger && 'text-gray-400 dark:text-gray-500'
                      )}
                    >
                      {item.icon}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{item.label}</div>
                    {item.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">{item.description}</div>
                    )}
                  </div>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}
